import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPaperclip, FiX } from 'react-icons/fi';

import { ATTACHMENT_RULES } from '../../constants';

/**
 * The attachment dropzone on both contact forms.
 *
 * Figma node 16383:29875: the field's own ground under a 1.6px dashed #cbcbcb
 * edge at a 12px radius, 17.6px of side and 21.6px of vertical padding, and a
 * centred column 8px apart — a 22px clip, a 13px line of instruction and an
 * 11px line of rules.
 *
 * The design draws the empty state only. Picked files are listed underneath as
 * chips, because a dropzone that swallows a file without showing it is the one
 * way this control can lie.
 *
 * Rules are read from `ATTACHMENT_RULES` for the accept attribute and the hint;
 * enforcing them is the hook's job, so the message beneath is in the reader's
 * language and says the same thing whichever form asked.
 */
const FileDrop = ({ id, label, files = [], onChange, error }) => {
    const { t } = useTranslation();
    const [isOver, setIsOver] = useState(false);

    const add = (incoming) => {
        const picked = Array.from(incoming || []);
        if (picked.length) onChange([...files, ...picked]);
    };

    const remove = (index) => onChange(files.filter((_, i) => i !== index));

    const handleDrop = (event) => {
        event.preventDefault();
        setIsOver(false);
        add(event.dataTransfer?.files);
    };

    const errorId = error ? `${id}-error` : undefined;

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <label htmlFor={id} className="tw-text-[13px] tw-font-normal tw-leading-[13px] tw-text-v2-text-deep">
                {label}
            </label>

            {/* A label rather than a div with a click handler: the whole zone
                becomes the file input's own hit area, so it opens the picker
                from a click, from Enter on the input, and from a screen reader,
                without this component minding the keyboard itself.

                It wraps the input instead of pointing at it with `htmlFor`.
                Doing both — a `for` on a label that also contains its control —
                is what makes a browser fire the click twice and open the picker
                twice. The label above already carries the `for`. */}
            <label
                onDragOver={(event) => { event.preventDefault(); setIsOver(true); }}
                onDragLeave={() => setIsOver(false)}
                onDrop={handleDrop}
                className={[
                    'tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2',
                    'tw-rounded-v2-btn tw-bg-v2-ground-200 tw-px-[17.6px] tw-py-[21.6px]',
                    'tw-border-[1.6px] tw-border-dashed tw-transition tw-duration-200',
                    'focus-within:tw-ring-2 focus-within:tw-ring-v2-brand/40',
                    isOver ? 'tw-border-v2-brand' : error ? 'tw-border-v2-error' : 'tw-border-v2-neutral-300',
                ].join(' ')}
            >
                <FiPaperclip className="tw-h-[22px] tw-w-[22px] tw-text-v2-text-faint" aria-hidden />

                <span className="tw-text-[13px] tw-leading-[19.5px] tw-text-v2-text-faint">
                    {t('v2.contact.attachments.pick')}
                </span>

                {/* The rules line names the formats and the size ceiling, and
                    both come from `ATTACHMENT_RULES` rather than the sentence,
                    so raising a limit changes one number in one file instead of
                    two translations that can quietly disagree with the code. */}
                <span className="tw-text-center tw-text-[11px] tw-leading-[16.5px] tw-text-v2-text-faint">
                    {t('v2.contact.attachments.hint', {
                        formats: ATTACHMENT_RULES.extensionsLabel,
                        megabytes: ATTACHMENT_RULES.maxBytesPerFile / (1024 * 1024),
                    })}
                </span>

                {/* `sr-only` rather than `hidden`: a hidden input is out of the
                    tab order, which would leave the zone unreachable by
                    keyboard. The value is cleared on every pick so choosing the
                    same file twice in a row still fires a change. */}
                <input
                    id={id}
                    type="file"
                    multiple
                    accept={ATTACHMENT_RULES.acceptAttribute}
                    aria-describedby={errorId}
                    onChange={(event) => { add(event.target.files); event.target.value = ''; }}
                    className="tw-sr-only"
                />
            </label>

            {files.length > 0 && (
                <ul className="tw-flex tw-flex-wrap tw-gap-2">
                    {files.map((file, index) => (
                        <li
                            key={`${file.name}-${index}`}
                            className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-v2-brand-tint tw-px-3 tw-py-1.5 tw-text-[12px] tw-text-v2-text-deep"
                        >
                            <span className="tw-max-w-[180px] tw-truncate">{file.name}</span>
                            <button
                                type="button"
                                onClick={() => remove(index)}
                                aria-label={t('v2.contact.attachments.remove', { name: file.name })}
                                className="tw-text-v2-text-faint hover:tw-text-v2-error tw-transition-colors"
                            >
                                <FiX className="tw-h-3.5 tw-w-3.5" aria-hidden />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {error && (
                <p id={errorId} className="tw-text-[12px] tw-leading-[18px] tw-text-v2-error">
                    {error}
                </p>
            )}
        </div>
    );
};

export default FileDrop;
